import {
  TransformationVariant,
  AssetType,
  ProviderLogo,
  SourceAsset,
  Genre,
} from '@amzn/fire-tv-campaign-automation-service';
import { KatDropdown, KatFileUpload } from '@amzn/katal-react';

import { CTA } from './configs/ctaConfiguration';
import { PreviewState } from './configs/previewConfigurations/types';

enum InputType {
  UPLOAD = 'upload',
  URL = 'url',
  SEARCH = 'search',
  LOGO_TEXT = 'logo-text',
  TEXT = 'text',
  DROP_DOWN = 'drop-down',
  DATE_TIME = 'date-time',
}

/**
 * Enumeration of possible asset processing states in the asset input workflow.
 *
 * @enum {string}
 */
enum AssetStatus {
  COMPLETED = 'COMPLETED',

  FAILED = 'FAILED',

  PENDING_INPUT = 'PENDING_INPUT',

  PENDING_UPLOAD = 'PENDING_UPLOAD',

  PENDING_VALIDATION = 'PENDING_VALIDATION',

  BASIC_VALIDATION_FAILED = 'BASIC_VALIDATION_FAILED',
  BASIC_VALIDATION_SUCCESS = 'BASIC_VALIDATION_SUCCESS',

  PENDING_TRANSFORMATION = 'PENDING_TRANSFORMATION',

  TRANSFORMATION_IN_PROGRESS = 'TRANSFORMATION_IN_PROGRESS',
}

interface IAssetInputHandler {
  onTransformationVariantChange(
    transformationVariantChangeEvent: KatDropdown.ChangeEvent,
    assetDetails: AssetDetails,
    status: AssetStatus,
  ): void;
  clearError(): void;
  handleFileAttached(fileAttachedEvent: KatFileUpload.FilesAttachedEvent): void;
  handleFileAttachedError(fileAttachedErrorEvent: KatFileUpload.FilesAttachedErrorEvent): void;
  resetAssetState(): void;
  handleFileReplaced(fileReplacedEvent: KatFileUpload.FilesReplacedEvent): void;
  handleUrl(urlInput: string): void;
}

interface ImageInputState {
  topOffset?: number;
  leftOffset?: number;
}
interface LogoTextState {
  logoTitle?: string;
  logoDescription?: string;
  cta?: CTA;
  providerLogo?: ProviderLogo;
}

interface VideoInputState {
  startTime?: string;
  endTime?: string;
  videoDuration?: number;
  mediaText?: Record<string, string>;
  genres?: Genre[];
}

interface OverlayState {
  contentBadge?: string;
  disclaimer?: string;
  contentRating?: string;
  overlayToggles?: Record<string, boolean>;
}

interface AssetState extends LogoTextState, VideoInputState, ImageInputState, OverlayState {
  key?: string;
  error?: string;
  status: AssetStatus;
  transformationVariant: TransformationVariant;
  inputType: InputType;
  sourceAssetKeys?: SourceAsset[];
}

interface Coordinates {
  x: number;
  y: number;
}

interface StateConfig {
  containerOffset: number;
  logo: { x: number; y: number };
  background: { x: number; y: number };
  overlay: { x: number; y: number };
}

/**
 * Interface representing the preview assets for a campaign
 * @interface PreviewAssets
 */
interface PreviewAssets {
  /** Optional URL for the background image asset */
  [AssetType.BACKGROUND_IMAGE]?: string;
  /** Optional URL for the logo asset */
  [AssetType.LOGO]?: string;
  /** Optional URL for the video trailer asset */
  [AssetType.VIDEO_TRAILER]?: string;
  /** Optional text for the Body Copy Asset */
  bodyCopy?: string;
}

/**
 * Interface representing the state of the preview section
 * @interface PreviewSectionState
 * @property {Partial<Record<PreviewState, string>>} urls - Map of state keys to preview URLs
 * @property {string} error - Error message if preview loading fails
 * @property {boolean} isLoading - Loading state indicator
 * @property {PreviewState} activeState - Current preview state
 */
interface PreviewSectionState {
  urls: Partial<Record<PreviewState, string>>;
  error?: string;
  isLoading: boolean;
  activeState: PreviewState;
}

interface AssetDetails {
  key?: string;
  file?: File;
}

export enum METRIC_NAME {
  BROWSER_COUNT = 'BrowserCount',
  FILE_UPLOAD_SUCCESS = 'FileUploadSuccess',
  FILE_UPLOAD_FAILED = 'FileUploadFailed',

  // URL Asset processing failures
  URL_ASSET_PROCESSING_FAILURE = 'UrlAssetProcessingFailure',

  // Latency metrics
  FILE_UPLOAD_LATENCY = 'FileUploadLatency',
  TRANSFORMATION_API_LATENCY = 'TransformationApiLatency',
  TRANSFORMATION_POLL_LATENCY = 'TransformationPollLatency',
  CAMPAIGN_CREATION_API_LATENCY = 'CampaignCreationApiLatency',
  CAMPAIGN_CREATION_POLL_LATENCY = 'CampaignCreationPollLatency',
  SEARCH_LATENCY = 'SearchLatency',
  DOWNLOAD_LATENCY = 'DownloadLatency',
  PREVIEW_GENERATION_LATENCY = 'PreviewGenerationLatency',

  // Feature success/failure
  TRANSFORMATION_SUCCESS = 'TransformationSuccess',
  TRANSFORMATION_FAILURE = 'TransformationFailure',
  CAMPAIGN_CREATION_SUCCESS = 'CampaignCreationSuccess',
  CAMPAIGN_CREATION_FAILURE = 'CampaignCreationFailure',
  SEARCH_SUCCESS = 'SearchSuccess',
  SEARCH_FAILURE = 'SearchFailure',
  DOWNLOAD_SUCCESS = 'DownloadSuccess',
  DOWNLOAD_FAILURE = 'DownloadFailure',
  PREVIEW_GENERATION_SUCCESS = 'PreviewGenerationSuccess',
  PREVIEW_GENERATION_FAILURE = 'PreviewGenerationFailure',
  URL_ASSET_PROCESSING_SUCCESS = 'UrlAssetProcessingSuccess',
  POLLING_TIMEOUT = 'PollingTimeout',
  POLLING_MAX_RETRIES = 'PollingMaxRetries',
  POLLING_ERROR = 'PollingError',

  // Authentication
  AUTHENTICATION_SUCCESS = 'AuthenticationSuccess',
  AUTHENTICATION_FAILURE = 'AuthenticationFailure',

  // Reliability
  TOKEN_REFRESH_FAILURE = 'TokenRefreshFailure',
  ERROR_BOUNDARY_TRIGGERED = 'ErrorBoundaryTriggered',

  // Latency
  AUTHENTICATION_LATENCY = 'AuthenticationLatency',
  URL_ASSET_PROCESSING_LATENCY = 'UrlAssetProcessingLatency',
}

export enum METRIC_UNIT {
  COUNT = 'Count',
  MILLISECONDS = 'Milliseconds',
}

export enum DIMENSION_KEYS {
  USER_ID = 'UserId',
  BROWSER = 'Browser',
  ASSET_TYPE = 'AssetType',
  POLLER_TYPE = 'PollerType',
  MARKETPLACE = 'Marketplace',
  DEVICE_FAMILY = 'DeviceFamily',
  PLACEMENT = 'Placement',
  TRANSFORMATION_VARIANT = 'TransformationVariant',
}

export { AssetStatus, InputType };
export type {
  IAssetInputHandler,
  ImageInputState,
  LogoTextState,
  VideoInputState,
  OverlayState,
  AssetState,
  PreviewState,
  StateConfig,
  PreviewAssets,
  PreviewSectionState,
  AssetDetails,
};
